import { BusinessObjectBase, decodeDecimals } from './base';
import { accountSchema } from '@schemas/business-objects/account';
import type { AccountUser } from './account-user';
import type { SupportAttachment } from './support-attachment';
import type { SupportCase } from './support-case';
import type { SupportComment } from './support-comment';
import type { CalendarDate } from '../base-types';
import type { TenantConfig } from '../interface-definitions/index';
import type { User } from './user';

export interface AccountData {
  readonly id: number;
  readonly name?: string;
  readonly designerSeats?: number;
  readonly renewalDate?: CalendarDate;
  readonly slackWebhookUrl?: string;
  readonly accountUsers?: AccountUser[];
  readonly cases?: SupportCase[];
  readonly comments?: SupportComment[];
  readonly attachments?: SupportAttachment[];
}

export interface Account extends Readonly<AccountData> {}
type AccountCreateWithChildrenData = Partial<
  Omit<AccountData, 'accountUsers' | 'cases'>
> & { accountUsers?: Partial<AccountUser>[]; cases?: Partial<SupportCase>[] };

export class Account extends BusinessObjectBase {
  static readonly entityName = 'Account' as const;
  static readonly plural = 'accounts';
  static schema = accountSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Account[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Account(data));
  }

  static async findOne(filter?: any): Promise<Account | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Account(data) : null;
  }

  static async findById(id: number, filter?: any): Promise<Account> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Account(data);
  }

  static async create(data: Omit<AccountData, 'id'>): Promise<Account> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Account(result);
  }

  static async createMany(data: Omit<AccountData, 'id'>[]): Promise<Account[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Account(item));
  }

  static async createWithChildren(
    data: AccountCreateWithChildrenData,
  ): Promise<Account> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new Account(result);
  }

  static async createManyWithChildren(
    data: AccountCreateWithChildrenData[],
  ): Promise<Account[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Account(item));
  }

  static async updateById(
    id: number,
    updates: Partial<AccountData>,
  ): Promise<Account> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Account(result);
  }

  static async deleteById(id: number): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async addMember(email: string, admin?: boolean): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Account.plural}/${(this as any).id}/add-member`;
    return BusinessObjectBase.post<any>(url, { email, admin });
  }
  static async currentUser(filter: any): Promise<any[]> {
    const url = `${this.baseUrl}/${this.plural}/current-user`;
    const queryParams = new URLSearchParams();
    if (filter !== undefined) queryParams.set('filter', JSON.stringify(filter));
    const qs = queryParams.toString();
    const fullUrl = qs ? `${url}?${qs}` : url;
    return this.get<any[]>(fullUrl);
  }
  async members(): Promise<any[]> {
    const url = `${BusinessObjectBase.baseUrl}/${Account.plural}/${(this as any).id}/members`;
    return BusinessObjectBase.get<any[]>(url);
  }
  async addMembers(body: { emails: string }): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Account.plural}/${(this as any).id}/add-members`;
    return BusinessObjectBase.post<any>(url, body);
  }
  static async findOneWithProfile(filter: any): Promise<Account | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one-with-profile`;
    return this.post<Account | null>(url, filter).then((value) =>
      decodeDecimals(accountSchema, value),
    );
  }
  async setSlackWebhookUrl(webhookUrl: string | null): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Account.plural}/${(this as any).id}/set-slack-webhook-url`;
    return BusinessObjectBase.post<any>(url, { webhookUrl });
  }
}
